// SAFETY MANAGEMENT SYSTEM — Reservo
// Required under: Point to Point Transport (Taxis and Hire Vehicles) Act 2016 (NSW)
// References: NSW Point to Point Transport Commissioner Safety Management System Guidance

export const SAFETY_MANAGEMENT_SYSTEM = {
  id: 'safety-management-system',
  title: 'Safety Management System',
  version: 'v1.0-2026-05-17',
  effectiveDate: '17/05/2026',
  audience: 'all',
  content: `SAFETY MANAGEMENT SYSTEM — RESERVO

Status: DRAFT — pending NSW Point to Point Transport Commissioner review

1. PURPOSE

This Safety Management System (SMS) describes how Reservo (David Williams, ABN 50 639 154 277, trading as Reservo) identifies, manages, and continuously improves safety risks associated with the provision of booking services for point-to-point passenger transport in New South Wales.

This SMS is maintained in accordance with Reservo's obligations as an authorised Booking Service Provider under the Point to Point Transport (Taxis and Hire Vehicles) Act 2016 (NSW) and the Point to Point Transport Regulation 2017.

2. SCOPE

This SMS applies to:
  (a) all bookings facilitated through the Reservo platform;
  (b) all Drivers authorised to accept bookings on the Reservo platform;
  (c) all vehicles registered for use on the Reservo platform;
  (d) all Reservo administrative and operational personnel.

3. ACCOUNTABILITY

3.1 The Operator (David Williams) is the accountable officer for safety under this SMS.

3.2 The Operator's responsibilities include:
  (a) approving and maintaining this SMS;
  (b) ensuring resources are available to implement the SMS;
  (c) chairing safety reviews at least quarterly;
  (d) reporting Notifiable Occurrences to the Commissioner within statutory timeframes;
  (e) cooperating with audits and inspections by the Commissioner.

4. RISK MANAGEMENT

4.1 Identified risk categories:
  (a) driver fatigue and impairment;
  (b) vehicle defects or unsafe modifications;
  (c) passenger assault, harassment, or sexual misconduct;
  (d) driver assault or harassment of passengers;
  (e) collisions and traffic incidents;
  (f) vulnerable passenger safety (minors, elderly, intoxicated, disabled);
  (g) accessibility refusal or non-compliance;
  (h) data breach affecting passenger or driver information;
  (i) platform failure causing safety incidents;
  (j) impersonation or identity fraud (driver or passenger).

4.2 Each risk has documented controls maintained in the Reservo Risk Register (separate document, reviewed quarterly).

4.3 The Operator reviews risk effectiveness at quarterly safety reviews using:
  (a) incident reports;
  (b) complaint trends;
  (c) audit findings;
  (d) driver behaviour scoring;
  (e) regulatory guidance updates.

5. SAFETY CONTROLS — TECHNICAL

The Reservo platform implements the following automated safety controls:

5.1 Driver authorisation:
  (a) NSW Driver Authorisation verification at onboarding and annually thereafter;
  (b) Working With Children Check verification where applicable;
  (c) criminal history check at onboarding and annually thereafter;
  (d) document expiry tracking with automated reminders at 30, 7, and 1 days before expiry;
  (e) automatic blocking of bookings if any compliance document is expired.

5.2 Driver fatigue management:
  (a) daily hour cap (10 hours active driving in any 24-hour period);
  (b) weekly hour cap (50 hours in any 7-day period);
  (c) consecutive day cap (6 consecutive working days);
  (d) automatic lockout when limits reached.

5.3 Pre-trip controls:
  (a) pre-trip checklist required before driver shift commencement;
  (b) vehicle safety self-declaration logged.

5.4 Passenger safety during trip:
  (a) Trip PIN verification between passenger and driver;
  (b) GPS tracking throughout active trip;
  (c) in-app SOS button connected to operations team;
  (d) in-app messaging with profanity filtering and post-trip access restriction.

5.5 Post-trip controls:
  (a) bidirectional ratings;
  (b) complaint submission with 24-hour SLA acknowledgement, 7-day SLA initial response, 28-day SLA resolution;
  (c) incident reporting with severity classification and statutory timeframe tracking.

6. SAFETY CONTROLS — OPERATIONAL

6.1 Driver onboarding:
  (a) identity verification (government-issued photo ID);
  (b) right-to-work verification;
  (c) reference and background checks where indicated;
  (d) completion of mandatory training modules covering: anti-discrimination, accessibility, child safety, vulnerable passenger handling, emergency response, complaint handling;
  (e) pass-mark on training module quizzes.

6.2 Vehicle authorisation:
  (a) registration and CTP insurance verification;
  (b) annual safety inspection sighting;
  (c) accessibility features declared and verified.

6.3 Incident response:
  (a) all incidents logged in the Reservo incident register;
  (b) severity classification by type (critical / high / medium / low);
  (c) critical incidents trigger SMS and email to Operator within minutes;
  (d) high-severity incidents trigger email to Operator within the hour;
  (e) Notifiable Occurrences reported to the Commissioner within statutory timeframes (typically 24 hours for serious matters).

6.4 Complaint response:
  (a) acknowledgement within 24 hours of submission;
  (b) initial substantive response within 7 days;
  (c) resolution target within 28 days;
  (d) serious complaints referred to the Commissioner.

7. INDUCTION AND TRAINING

7.1 Drivers complete mandatory training modules at onboarding and refresher modules annually.

7.2 Training records are retained for 7 years.

7.3 Modules cover: defensive driving, fatigue management, anti-discrimination, disability awareness, assistance animal handling, child safety, vulnerable passenger interaction, emergency response, complaint handling, fraud and platform integrity.

8. RECORD KEEPING

8.1 All safety records are retained for a minimum of 7 years:
  (a) driver authorisations and renewals;
  (b) vehicle authorisations and inspections;
  (c) trip records including GPS traces;
  (d) incident reports and investigations;
  (e) complaint records;
  (f) training records;
  (g) audit log of administrative actions;
  (h) consent records.

8.2 Records are available to the Commissioner on request within the statutory response period.

9. EMERGENCY RESPONSE

9.1 In a life-threatening emergency, the relevant party (driver, passenger, or witness) contacts 000 first.

9.2 The in-app SOS feature alerts Reservo Operations who:
  (a) acknowledge SOS within 5 minutes;
  (b) coordinate with emergency services as appropriate;
  (c) notify the passenger's nominated emergency contact if escalation occurs;
  (d) log the event in the incident register.

10. AUDITING AND CONTINUOUS IMPROVEMENT

10.1 The Operator conducts internal safety audits at least quarterly.

10.2 Audit topics rotate through:
  (a) driver document compliance;
  (b) vehicle compliance;
  (c) incident response effectiveness;
  (d) complaint handling effectiveness;
  (e) accessibility compliance;
  (f) data protection compliance.

10.3 Audit findings are documented; corrective actions tracked through to closure.

11. COOPERATION WITH AUTHORITIES

11.1 Reservo cooperates fully with:
  (a) NSW Police;
  (b) NSW Point to Point Transport Commissioner;
  (c) Transport for NSW;
  (d) Office of the Australian Information Commissioner;
  (e) Australian Human Rights Commission;
  (f) any other authority with valid jurisdiction.

12. REVIEW

12.1 This SMS is reviewed at least annually and whenever:
  (a) the regulatory landscape changes materially;
  (b) a significant incident reveals control deficiencies;
  (c) the Commissioner directs a review.

12.2 Version history is maintained at the foot of this document.

13. CONTACT

David Williams trading as Reservo
ABN: 50 639 154 277
Email: admin@reservo.au
Phone: 0435 679 630
Address: Unit 6/297 Miller Road, Bass Hill NSW 2197

---
VERSION HISTORY
v1.0 — 17/05/2026 — Initial draft for solicitor and Commissioner review.

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW. It has not been reviewed by an Australian-qualified legal practitioner.`,
};